import React, { useState, useEffect } from 'react';
import { TokenData } from '../hooks/useBondingCurve';
import TokenCard from './TokenCard';
import { MIGRATION_THRESHOLD } from '../config/contracts';
import { fetch24hPrices } from '../lib/supabase';

interface MarketPageProps {
  tokens: TokenData[];
  onSelectToken: (token: TokenData) => void;
  onRefresh: () => void;
  loading: boolean;
  onAddToken: () => void;
  account: string | null;
}

type FilterType = 'all' | 'active' | 'migrated';
type SortType = 'newest' | 'oldest' | 'price_high' | 'price_low' | 'reserve_high';

const MarketPage: React.FC<MarketPageProps> = ({
  tokens,
  onSelectToken,
  onRefresh,
  loading,
  onAddToken,
  account,
}) => {
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [sort, setSort] = useState<SortType>('newest');
  const [displayTokens, setDisplayTokens] = useState<TokenData[]>([]);
  const [prices24h, setPrices24h] = useState<Record<string, string>>({});

  useEffect(() => {
    fetch24hPrices().then(setPrices24h).catch(() => {});
  }, []);

  const sourceTokens = tokens;

  useEffect(() => {
    let result = [...sourceTokens];

    // Filter
    if (filter === 'active') result = result.filter(t => t.active && !t.migrated);
    else if (filter === 'migrated') result = result.filter(t => t.migrated);

    // Search
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.symbol.toLowerCase().includes(q) ||
        t.tokenAddress.toLowerCase().includes(q)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sort) {
        case 'newest': return b.createdAt - a.createdAt;
        case 'oldest': return a.createdAt - b.createdAt;
        case 'price_high': return parseFloat(b.currentPrice) - parseFloat(a.currentPrice);
        case 'price_low': return parseFloat(a.currentPrice) - parseFloat(b.currentPrice);
        case 'reserve_high': return parseFloat(b.reserveUSDT) - parseFloat(a.reserveUSDT);
        default: return 0;
      }
    });

    setDisplayTokens(result);
  }, [sourceTokens, filter, sort, search]);

  const BASE_RESERVE = 10000; // 平台基础储备保底值
  const chainReserve = sourceTokens.reduce((s, t) => s + parseFloat(t.reserveUSDT || '0'), 0);
  const stats = {
    total: sourceTokens.length,
    active: sourceTokens.filter(t => t.active && !t.migrated).length,
    migrated: sourceTokens.filter(t => t.migrated).length,
    totalReserve: BASE_RESERVE + chainReserve,
  };

  return (
    <div style={styles.page}>
      {/* Hero Stats */}
      <div style={styles.heroStats}>
        <div style={styles.heroStat}>
          <div style={styles.heroStatValue}>{stats.total}</div>
          <div style={styles.heroStatLabel}>总代币数</div>
        </div>
        <div style={styles.heroDivider} />
        <div style={styles.heroStat}>
          <div style={{ ...styles.heroStatValue, color: '#00e676' }}>{stats.active}</div>
          <div style={styles.heroStatLabel}>曲线交易中</div>
        </div>
        <div style={styles.heroDivider} />
        <div style={styles.heroStat}>
          <div style={{ ...styles.heroStatValue, color: '#ffd600' }}>{stats.migrated}</div>
          <div style={styles.heroStatLabel}>已迁移 PancakeSwap</div>
        </div>
        <div style={styles.heroDivider} />
        <div style={styles.heroStat}>
          <div style={{ ...styles.heroStatValue, color: '#7c4dff' }}>
            ${stats.totalReserve.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div style={styles.heroStatLabel}>总 USDT 储备</div>
        </div>
        <div style={styles.heroDivider} />
        <div style={styles.heroStat}>
          <div style={{ ...styles.heroStatValue, color: '#00d4ff' }}>
            ${parseInt(MIGRATION_THRESHOLD).toLocaleString()}
          </div>
          <div style={styles.heroStatLabel}>迁移阈值</div>
        </div>
      </div>

      {/* Controls */}
      <div style={styles.controls}>
        <div style={styles.searchWrap}>
          <span style={styles.searchIcon}>🔍</span>
          <input
            type="text"
            style={styles.searchInput}
            placeholder="搜索代币名称、符号或合约地址..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div style={styles.filterGroup}>
          {(['all', 'active', 'migrated'] as FilterType[]).map(f => (
            <button
              key={f}
              style={{
                ...styles.filterBtn,
                ...(filter === f ? styles.filterBtnActive : {}),
              }}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? '全部' : f === 'active' ? '交易中' : '已迁移'}
            </button>
          ))}
        </div>

        <select
          style={styles.sortSelect}
          value={sort}
          onChange={e => setSort(e.target.value as SortType)}
        >
          <option value="newest">最新添加</option>
          <option value="oldest">最早添加</option>
          <option value="price_high">价格最高</option>
          <option value="price_low">价格最低</option>
          <option value="reserve_high">储备最多</option>
        </select>

        <button style={styles.refreshBtn} onClick={onRefresh} disabled={loading}>
          {loading ? '⟳' : '↻'} 刷新
        </button>

        <button style={styles.addBtn} onClick={onAddToken}>
          + 添加代币
        </button>
      </div>

      {/* Token Grid */}
      {loading ? (
        <div style={styles.loadingGrid}>
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} style={styles.skeletonCard}>
              <div style={styles.skeletonHeader} />
              <div style={styles.skeletonLine} />
              <div style={{ ...styles.skeletonLine, width: '70%' }} />
              <div style={styles.skeletonBar} />
            </div>
          ))}
        </div>
      ) : displayTokens.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>🔍</div>
          <div style={styles.emptyTitle}>未找到匹配的代币</div>
          <div style={styles.emptyDesc}>
            {search ? `没有与 "${search}" 匹配的代币` : '暂无代币，点击添加代币开始吧'}
          </div>
          {!search && (
            <button style={styles.emptyAddBtn} onClick={onAddToken}>
              + 添加第一个代币
            </button>
          )}
        </div>
      ) : (
        <div style={styles.grid}>
          {displayTokens.map(token => (
            <TokenCard
              key={token.tokenAddress}
              token={token}
              onClick={onSelectToken}
              price24hAgo={prices24h[token.tokenAddress.toLowerCase()]}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 16,
  },
  heroStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 16,
    overflow: 'hidden',
  },
  heroStat: {
    textAlign: 'center' as const,
    padding: '16px 12px',
    borderRight: '1px solid #1e2d4a',
    borderBottom: '1px solid #1e2d4a',
  },
  heroStatValue: {
    fontSize: 22,
    fontWeight: 800,
    color: '#e8eef7',
    lineHeight: 1,
    marginBottom: 6,
  },
  heroStatLabel: {
    fontSize: 11,
    color: '#6b7d99',
    fontWeight: 500,
  },
  heroDivider: { display: 'none' },
  controls: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  searchWrap: {
    flex: '1 1 100%',
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute' as const,
    left: 12,
    fontSize: 14,
  },
  searchInput: {
    width: '100%',
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 12,
    color: '#e8eef7',
    fontSize: 14,
    padding: '12px 16px 12px 38px',
    outline: 'none',
    boxSizing: 'border-box' as const,
  },
  filterGroup: {
    display: 'flex',
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 10,
    padding: 3,
    gap: 3,
    flex: '1 1 auto',
  },
  filterBtn: {
    flex: 1,
    background: 'transparent',
    border: 'none',
    color: '#6b7d99',
    fontSize: 12,
    fontWeight: 500,
    padding: '7px 10px',
    borderRadius: 7,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  filterBtnActive: {
    background: 'rgba(0,212,255,0.12)',
    color: '#00d4ff',
  },
  sortSelect: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 10,
    color: '#e8eef7',
    fontSize: 12,
    padding: '9px 10px',
    outline: 'none',
    cursor: 'pointer',
    flex: '0 0 auto',
  },
  refreshBtn: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid #1e2d4a',
    color: '#6b7d99',
    fontSize: 13,
    fontWeight: 600,
    padding: '9px 14px',
    borderRadius: 10,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  addBtn: {
    background: 'linear-gradient(135deg,#00d4ff,#7c4dff)',
    border: 'none',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    padding: '9px 16px',
    borderRadius: 10,
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
    flex: '0 0 auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 12,
  },
  loadingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 12,
  },
  skeletonCard: {
    background: '#131929',
    border: '1px solid #1e2d4a',
    borderRadius: 14,
    padding: '16px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 10,
  },
  skeletonHeader: {
    height: 44,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 8,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonLine: {
    height: 14,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 5,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  skeletonBar: {
    height: 5,
    background: 'rgba(255,255,255,0.04)',
    borderRadius: 3,
    animation: 'pulse 1.5s ease-in-out infinite',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    padding: '60px 20px',
    gap: 14,
    textAlign: 'center' as const,
  },
  emptyIcon: { fontSize: 44 },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: '#e8eef7' },
  emptyDesc: { fontSize: 13, color: '#6b7d99' },
  emptyAddBtn: {
    marginTop: 8,
    background: 'linear-gradient(135deg,#00d4ff,#7c4dff)',
    border: 'none',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    padding: '13px 28px',
    borderRadius: 12,
    cursor: 'pointer',
  },
};

export default MarketPage;
